import React, { useState } from 'react';
import axios from 'axios';
import { endpoints } from '../api';
import './Demo.css';

const Demo = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [statusResult, setStatusResult] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [customApiUrl, setCustomApiUrl] = useState('');

    const validatePhoneNumber = (num) => {
        const cleaned = num.trim();
        if (!cleaned) {
            return "Phone number is required.";
        }
        if (cleaned.length < 5) {
            return `Phone number '${cleaned}' looks too short (min 5 digits).`;
        }
        return "";
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setPhoneNumber(val);
        if (validationError) {
            setValidationError('');
        }
    };

    const handleTriggerCall = async (e) => {
        if (e) e.preventDefault();
        
        const error = validatePhoneNumber(phoneNumber);
        if (error) {
            setValidationError(error);
            return;
        }

        setValidationError('');
        setLoading(true);
        setStatusResult(null);

        const targetNumber = phoneNumber.trim();

        try {
            let response;
            
            if (customApiUrl.trim()) {
                let targetUrl = customApiUrl.trim();
                if (!targetUrl.endsWith('/make-call') && !targetUrl.includes('?')) {
                    targetUrl = `${targetUrl.replace(/\/$/, '')}/make-call`;
                }
                response = await axios.post(targetUrl, { to: targetNumber }, {
                    headers: { 'Content-Type': 'application/json' }
                });
            } else {
                try {
                    response = await endpoints.triggerCall(targetNumber);
                } catch (firstErr) {
                    // Try direct localhost:5000 fallback if main endpoint fails
                    response = await axios.post('http://localhost:5000/make-call', { to: targetNumber }, {
                        headers: { 'Content-Type': 'application/json' }
                    });
                }
            }

            setStatusResult({
                success: true,
                message: "Call dispatched successfully!",
                status: response.status,
                data: response.data
            });
        } catch (err) {
            console.error("Error triggering call:", err);
            const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || "Failed to trigger call";
            setStatusResult({
                success: false,
                message: errorMsg,
                status: err.response?.status || 'NETWORK_ERROR',
                data: err.response?.data || null
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="demo-container">
            <div className="demo-card">
                <div className="demo-badge">
                    <span className="demo-badge-dot"></span>
                    Secret Route / Demo
                </div>

                <div className="demo-header">
                    <h1>LiveKit Outbound Call Agent</h1>
                    <p>Enter a phone number to dispatch an automated AI call agent session via LiveKit.</p>
                </div>

                <form className="demo-form" onSubmit={handleTriggerCall}>
                    <div>
                        <label className="demo-input-label">Target Phone Number</label>
                        <div className="demo-input-wrapper">
                            <span className="demo-input-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                </svg>
                            </span>
                            <input
                                type="tel"
                                className="demo-input"
                                placeholder="9876543210"
                                value={phoneNumber}
                                onChange={handleInputChange}
                                disabled={loading}
                                autoFocus
                            />
                        </div>
                        <div className="demo-hint">Enter target phone number (e.g. 9876543210)</div>
                        
                        {validationError && (
                            <div className="demo-error-msg">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {validationError}
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="demo-btn"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="demo-spinner"></span>
                                Initiating Call...
                            </>
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                                Trigger Call
                            </>
                        )}
                    </button>
                </form>

                {statusResult && (
                    <div className={`demo-result-card ${statusResult.success ? 'demo-result-success' : 'demo-result-error'}`}>
                        <div className="demo-result-header">
                            {statusResult.success ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="15" y1="9" x2="9" y2="15"/>
                                    <line x1="9" y1="9" x2="15" y2="15"/>
                                </svg>
                            )}
                            {statusResult.message} (HTTP {statusResult.status})
                        </div>
                        {statusResult.data && (
                            <div className="demo-json-box">
                                {typeof statusResult.data === 'object' 
                                    ? JSON.stringify(statusResult.data, null, 2) 
                                    : String(statusResult.data)}
                            </div>
                        )}
                    </div>
                )}

                <div className="demo-custom-api-toggle">
                    <span onClick={() => setShowConfig(!showConfig)}>
                        {showConfig ? '▼ Hide API Endpoint Setting' : '⚙️ Custom API Endpoint Override'}
                    </span>
                    {showConfig && (
                        <div>
                            <input
                                type="text"
                                className="demo-custom-api-input"
                                placeholder="http://127.0.0.1:5000 or custom base URL"
                                value={customApiUrl}
                                onChange={(e) => setCustomApiUrl(e.target.value)}
                            />
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                                Default: http://localhost:5000/make-call
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Demo;
